import { FloorPlan3DMaterialSlot } from "../products/product3DTypes";
import {
  BuiltInFurnitureMaterial,
  getBuiltInFurnitureMaterial,
  listBuiltInFurnitureMaterials,
} from "./builtInFurnitureMaterials";
import {
  getPrimitiveSymbol3DModelKey,
  getPrimitiveSymbolMaterialSlots,
} from "../symbols/registry/primitiveSymbol3DConfig";

/**
 * Named configurable part on a product (Top, Base, Upholstery, …).
 * Each option maps to a materialAssignments slot and a curated preset list.
 */
export type ProductMaterialOptionKey =
  | "topMaterial"
  | "baseMaterial"
  | "doorMaterial"
  | "panelMaterial"
  | "frameMaterial"
  | "handleMaterial"
  | "hardwareMaterial"
  | "upholstery"
  | "material";

export type ProductMaterialOptionDefinition = {
  slot: FloorPlan3DMaterialSlot;
  /**
   * Built-in furniture material ids allowed for this option.
   * Prefer `allStandardMaterials` for shape/stairs so the pack stays in sync
   * with admin Standard materials (no hardcoded subset).
   */
  materials?: readonly string[];
  /** When true, expose every built-in/standard material (admin pack). */
  allStandardMaterials?: boolean;
  labelKey: string;
};

export type ProductMaterialOptionsConfig = Partial<
  Record<ProductMaterialOptionKey, ProductMaterialOptionDefinition>
>;

export type ProductMaterialOptionsTargetKind =
  | "primitive"
  | "stairs"
  | "shape"
  | "door";

export type ProductMaterialOptionsTarget = {
  kind: ProductMaterialOptionsTargetKind;
  /** Primitive model key (e.g. kitchen:cabinet) when kind is primitive. */
  modelKey?: string | null;
};

const CABINET_OPTIONS: ProductMaterialOptionsConfig = {
  topMaterial: {
    slot: "doorPanel",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.topMaterial",
  },
  baseMaterial: {
    slot: "furnitureWood",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.baseMaterial",
  },
  doorMaterial: {
    slot: "furnitureMetal",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.doorMaterial",
  },
};

/** Soft seating with metal legs (sofas, armchair, lounge). */
const UPHOLSTERY_AND_METAL_BASE_OPTIONS: ProductMaterialOptionsConfig = {
  upholstery: {
    slot: "furnitureFabric",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.upholstery",
  },
  baseMaterial: {
    slot: "furnitureMetal",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.baseMaterial",
  },
};

/** Chaise sofa: fabric body + wood legs. */
const UPHOLSTERY_AND_WOOD_BASE_OPTIONS: ProductMaterialOptionsConfig = {
  upholstery: {
    slot: "furnitureFabric",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.upholstery",
  },
  baseMaterial: {
    slot: "furnitureWood",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.baseMaterial",
  },
};

/** Screens: panel fabric/screen + metal frame/stand. */
const SCREEN_OPTIONS: ProductMaterialOptionsConfig = {
  material: {
    slot: "furnitureScreen",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.material",
  },
  baseMaterial: {
    slot: "furnitureMetal",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.baseMaterial",
  },
};

/** Legged tables: wood top + metal legs. */
const TABLE_TOP_AND_METAL_BASE_OPTIONS: ProductMaterialOptionsConfig = {
  topMaterial: {
    slot: "furnitureWood",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.topMaterial",
  },
  baseMaterial: {
    slot: "furnitureMetal",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.baseMaterial",
  },
};

/** Pedestal coffee tables: single wood material. */
const WOOD_MATERIAL_OPTIONS: ProductMaterialOptionsConfig = {
  material: {
    slot: "furnitureWood",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.material",
  },
};

/** Dining chairs: single metal material. */
const METAL_MATERIAL_OPTIONS: ProductMaterialOptionsConfig = {
  material: {
    slot: "furnitureMetal",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.material",
  },
};

/** Sinks: porcelain/counter deck + metal fixtures. */
const SINK_OPTIONS: ProductMaterialOptionsConfig = {
  topMaterial: {
    slot: "doorPanel",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.topMaterial",
  },
  baseMaterial: {
    slot: "furnitureMetal",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.baseMaterial",
  },
};

/** Lounge chairs: soft shell/cushion + hard base/legs. */
const LOUNGE_CHAIR_OPTIONS = UPHOLSTERY_AND_METAL_BASE_OPTIONS;

/** Stairs use the full Standard materials pack (same list as admin). */
const STAIRS_OPTIONS: ProductMaterialOptionsConfig = {
  material: {
    slot: "stair",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.material",
  },
};

/**
 * Opening doors: leaf (dörrblad) / frame+casing (karm/foder) / hardware (beslag).
 * Handles, hinges, and locks share the doorHardware slot.
 * Meshes already resolve these slots via materialAssignments.
 */
const DOOR_OPTIONS: ProductMaterialOptionsConfig = {
  panelMaterial: {
    slot: "doorPanel",
    allStandardMaterials: true,
    labelKey: "propertyPanel.materialSlot.doorPanel",
  },
  frameMaterial: {
    slot: "doorFrame",
    allStandardMaterials: true,
    labelKey: "propertyPanel.materialSlot.doorFrame",
  },
  hardwareMaterial: {
    slot: "doorHardware",
    allStandardMaterials: true,
    labelKey: "propertyPanel.materialSlot.doorHardware",
  },
};

/** Shapes use the full Standard materials pack (same list as admin). */
const SHAPE_OPTIONS: ProductMaterialOptionsConfig = {
  material: {
    slot: "wall",
    allStandardMaterials: true,
    labelKey: "propertyPanel.productOption.material",
  },
};

const PRODUCT_MATERIAL_OPTIONS_BY_MODEL_KEY: Partial<
  Record<string, ProductMaterialOptionsConfig>
> = {
  "kitchen:cabinet": CABINET_OPTIONS,
  "kitchen:sink-double": SINK_OPTIONS,
  "kitchen:sink-drainboard": SINK_OPTIONS,
  "kitchen:sink-single": SINK_OPTIONS,
  "living:sofa-2-seat": UPHOLSTERY_AND_METAL_BASE_OPTIONS,
  "living:sofa-3-seat": UPHOLSTERY_AND_METAL_BASE_OPTIONS,
  "living:sofa-modular": UPHOLSTERY_AND_METAL_BASE_OPTIONS,
  "living:sofa-chaise": UPHOLSTERY_AND_WOOD_BASE_OPTIONS,
  "living:lounge-chair-1": LOUNGE_CHAIR_OPTIONS,
  "living:lounge-chair-2": LOUNGE_CHAIR_OPTIONS,
  "living:armchair": UPHOLSTERY_AND_METAL_BASE_OPTIONS,
  "living:screen": SCREEN_OPTIONS,
  "living:screen-wide": SCREEN_OPTIONS,
  "living:coffee-table-octagonal": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "living:coffee-table-pentagonal": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "living:coffee-table-hexagonal": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "living:coffee-table-oval": WOOD_MATERIAL_OPTIONS,
  "living:coffee-table-organic": WOOD_MATERIAL_OPTIONS,
  "dining:table-plain": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:table-2-chair": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:table-4-chair": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:table-6-chair": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:round-table-plain": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:round-table": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:smart-table-rectangle": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "dining:smart-table-round": TABLE_TOP_AND_METAL_BASE_OPTIONS,
  "living:bookshelf": WOOD_MATERIAL_OPTIONS,
  "dining:armchair": METAL_MATERIAL_OPTIONS,
  "dining:chair": METAL_MATERIAL_OPTIONS,
};

/**
 * Returns curated material options for a selection target, or null when the
 * generic slot UI should be used instead.
 */
export function getProductMaterialOptions(
  target: ProductMaterialOptionsTarget,
): ProductMaterialOptionsConfig | null {
  if (target.kind === "stairs") {
    return STAIRS_OPTIONS;
  }
  if (target.kind === "door") {
    return DOOR_OPTIONS;
  }
  if (target.kind === "shape") {
    return SHAPE_OPTIONS;
  }
  if (!target.modelKey) {
    return null;
  }
  const resolved =
    getPrimitiveSymbol3DModelKey(target.modelKey) ?? target.modelKey;
  return PRODUCT_MATERIAL_OPTIONS_BY_MODEL_KEY[resolved] ?? null;
}

/**
 * Ordered option keys for UI tabs (stable product-facing order).
 */
export function listProductMaterialOptionKeys(
  options: ProductMaterialOptionsConfig,
): ProductMaterialOptionKey[] {
  const order: ProductMaterialOptionKey[] = [
    "topMaterial",
    "upholstery",
    "material",
    "panelMaterial",
    "frameMaterial",
    "baseMaterial",
    "doorMaterial",
    "handleMaterial",
    "hardwareMaterial",
  ];
  return order.filter((key) => options[key] !== undefined);
}

/**
 * Slots exposed by product options (for active-slot sync).
 */
export function listProductMaterialOptionSlots(
  options: ProductMaterialOptionsConfig,
): FloorPlan3DMaterialSlot[] {
  return listProductMaterialOptionKeys(options).map(
    (key) => options[key]!.slot,
  );
}

/**
 * Built-in materials allowed for a product option, preserving definition order.
 * `allStandardMaterials` resolves to the full admin Standard materials pack.
 */
export function getAllowedMaterialsForOption(
  option: ProductMaterialOptionDefinition,
): BuiltInFurnitureMaterial[] {
  if (option.allStandardMaterials) {
    return listBuiltInFurnitureMaterials();
  }
  return (option.materials ?? [])
    .map((id) => getBuiltInFurnitureMaterial(id))
    .filter((material): material is BuiltInFurnitureMaterial =>
      Boolean(material),
    );
}

/**
 * Find the product option key that owns a material slot.
 */
export function getProductMaterialOptionKeyForSlot(
  options: ProductMaterialOptionsConfig,
  slot: FloorPlan3DMaterialSlot,
): ProductMaterialOptionKey | undefined {
  return listProductMaterialOptionKeys(options).find(
    (key) => options[key]?.slot === slot,
  );
}

/**
 * Resolve material slots for a primitive: prefer product-options slots when
 * curated, otherwise the registry manifest.
 */
export function resolveMaterialSlotsForPrimitive(
  modelKey: string | null | undefined,
): FloorPlan3DMaterialSlot[] {
  const options = getProductMaterialOptions({
    kind: "primitive",
    modelKey,
  });
  if (options) {
    return listProductMaterialOptionSlots(options);
  }
  return getPrimitiveSymbolMaterialSlots(modelKey ?? undefined);
}
